import i18n, { t } from 'i18next';
import type { LDContext } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import { action, computed, makeObservable, observable } from 'mobx';

import { SOCKET_DISCONNECT_MULTISESSION_USER } from '../../config/constants';
import type { CleanGeoData, Territory } from '../../features/geography';
import { getUserLanguage } from '../../i18n/helpers';
import { analytics } from '../../services/analytics';
import { getRequest, postRequest } from '../../services/api';
import { auth, type Profile, type ThirdPartyStatus } from '../../services/auth';
import { logToNewRelic } from '../../services/debug';
import { createLDContext, identifyUser, ldClient, resolveFlag } from '../../services/launchDarkly';
import { isInShell } from '../../services/nativeBridge/shellPresence';
import { PHONE_OTP_ALREADY_VERIFIED, PhoneOtpError } from '../../services/phoneOtpError';
import { queryClient } from '../../services/queryClient';
import { ReferralService } from '../../services/referral.service';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { BUCKET_PREFIX, ENVIRONMENT } from '../../utils/environment';
import { getErrorMessage } from '../../utils/errors';
import { isCmsPages } from '../../utils/isExemptPage';
import { devError } from '../../utils/log';
import { getParam } from '../../utils/marketingHelper';
import { type RootStore } from '../RootStore';

import { LandingKey } from './types';

const LAST_LOGIN_TAB_ID = 'lastLoginTabId';

const DEFAULT_DIRECT_EM_BANNER = 'direct_em_150_offer';

type PendingActionKey =
  | 'signUp'
  | 'thirdPartyAuth'
  | 'signIn'
  | 'resendEmailVerification'
  | 'sendPhoneVerification'
  | 'completePhoneVerification'
  | 'sendPasswordResetLink'
  | 'chooseCountry';

type AuthStatus =
  | 'authenticated'
  | 'thirdPartyFirstLogin'
  | 'unauthenticated'
  | 'userInactive'
  | 'userSelfExcluded'
  | 'authError'
  | 'error';

export class AuthStore {
  private readonly rootStore: RootStore;

  // Possible auth flow scenarios:
  // a) User signs up with credentials. Callback is NEVER triggered since the email is not verified by
  //    the time firebase user arrives. `setUserDataReady(true)` is called within `signInToBackendAndAuthorize()`
  //    when email is verified, `setAuthStatus("authenticated")` is called when the SignUpWizard is closed.
  // b) User goes through SignUpWizard or LogInWizard with third-party auth. Callback is triggered with a non-null
  //    `user` and `thirdPartyStatus` being either "firstLogin" or "nonFirstLogin". `setUserDataReady(true)` gets called
  //    immediately, `authStatus` first becomes "thirdPartyFirstLogin" and then, when WelcomeBonusDialog (and
  //    SignUpPrivacy, if the user went via LogInWizard) dialogs are closed, changes to "authenticated".
  // c) User enters already being signed in, this triggers with non-null `user` and `thirdPartyStatus` being either
  //    "notFirstParty" or "nonFirstLogin".
  // d) User logs in with credentials. Callback is triggered with non-null `user` and
  //    `thirdPartyStatus` = "notThirdParty", user gets authorized immediately.
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    this.setLastLoginTabId();

    if (!isCmsPages()) {
      void auth.subscribeToAuthStateChange(async (user, thirdPartyStatus) => {
        this.isDisconnectedByMultisession = false;
        this.setUserDataReady(!!user);
        const { pendingEmailVerification } = thirdPartyStatus;
        this.setIsEmailVerificationPending(!user && !!pendingEmailVerification);
        if (!user && pendingEmailVerification) {
          // Normally set by the sign-up / log-in form, which a page reload skips.
          this.setCurrentUserEmail(pendingEmailVerification.email);
        }

        if (auth.isUserInactive) {
          this.setAuthStatus('userInactive');
        } else if (auth.isUserSelfExcluded) {
          this.setAuthStatus('userSelfExcluded');
        } else if (auth.isAuthError) {
          this.setAuthStatus('authError');
        } else if (user && thirdPartyStatus.status) {
          this.setAuthStatus(AuthStore.getAuthStatus(thirdPartyStatus.status));
        } else if (!user) {
          this.setAuthStatus('unauthenticated');
        }

        this.resyncUserCooldown();

        let ldContext: LDContext = {
          kind: 'user',
        };

        if (user) {
          this.setFlowData(thirdPartyStatus.data);
          this.setNewRelicUserAttributes(user);
          this.setupUserEnvironment(user);

          ldContext = createLDContext(user);

          // Handle socket connection based on multi-session prevention and tab ID
          if (!socket.connected) {
            this.handleSocketConnection();
          }
        } else {
          void analytics.logout();
          ldContext = createLDContext(null, this.rootStore.liveGeoStore.rawGeo);
          this.setupGuestEnvironment();
          this.rootStore.gameStore.sendSlotsGameUiMessage({ type: 'logout' });
          this.rootStore.locationStore.resetStore();
          this.rootStore.geoRestrictionStore.clean();
          socket.disconnect();
        }

        this.setupGeneralEnvironment();

        if (ldClient) {
          try {
            // Measured on prod: identify takes ~2s, because on('change') puts the SDK in streaming mode
            // and identify waits for the new context's first stream payload. Everything downstream of
            // this await is blocked on it — Lobby renders <Loader fullScreen/> until
            // isAuthenticationFlowDone, so the header, the lobby body and the LCP banner all wait.
            //
            // Flag values are already correct here: login returns ldBootstrapFlags, evaluated for this
            // user server-side, and render.tsx boots the client with them. So identify is keeping the
            // client's own context in sync, not fetching values we lack — it does not need to gate the
            // first render. The old 5s ceiling only ever fired on the identifyUser retry bug.
            const timeoutMs = 500;

            const result = await Promise.race([
              identifyUser(ldContext),
              new Promise((resolve) => setTimeout(() => resolve('timeout'), timeoutMs)),
            ]);

            if (result === 'timeout') {
              void console.warn(`IdentifyUser did not return within ${timeoutMs}ms; continuing on bootstrap flags`);
            } else if (result === 'failed') {
              // Distinct from a timeout: identify actually rejected, which points at LD config or
              // connectivity rather than at it merely being slow.
              void console.error('IdentifyUser failed; continuing on bootstrap flags');
            }
          } catch (error) {
            const errorMessage = getErrorMessage(error);
            console.error(`IdentifyUser failed: ${errorMessage}`);
            // optional: log the error if needed
          }
        }

        if (!this.isAuthenticationFlowDone) {
          this.setIsAuthenticationFlowDone(true);
        }
      });

      socket.on(SOCKET_DISCONNECT_MULTISESSION_USER, () => {
        this.handleMultiSessionDisconnect();
      });
    }
  }

  private handleSocketConnection() {
    const preventMultisessionFlag = resolveFlag('prevent_multisession_enable', false);
    if (preventMultisessionFlag) {
      const lastLoginTabId = localStorage.getItem(LAST_LOGIN_TAB_ID);
      if (lastLoginTabId === this.tabId) {
        socket.connect();
      } else {
        this.handleMultiSessionDisconnect();
      }
    } else {
      socket.connect();
    }
  }

  @action
  setLastLoginTabId() {
    localStorage.setItem(LAST_LOGIN_TAB_ID, this.tabId);
  }

  @action
  private handleMultiSessionDisconnect() {
    this.isDisconnectedByMultisession = true;
    socket.disconnect();
  }

  @computed
  get isSigningUp(): boolean {
    return this.pending('signUp') || this.pending('thirdPartyAuth');
  }

  private setNewRelicUserAttributes(user: Profile) {
    if (window.newrelic) {
      window.newrelic.setUserId(user.firebaseUid);
      window.newrelic.setCustomAttribute('firebaseUid', user.firebaseUid);
      if (user.userWallet.ownerId) {
        window.newrelic.setCustomAttribute('userId', user.userWallet.ownerId);
      }
    }
  }

  private prevLang = getUserLanguage();

  private setupGeneralEnvironment() {
    void this.rootStore.categoriesStore.fetchCategories();

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        void this.rootStore.categoriesStore.updateGameLabelConfigs(lang);
      }
    });

    void this.rootStore.cmsStore.fetchFooter();

    if (!this.rootStore.rewardsStore.welcomeBonus) {
      void this.rootStore.rewardsStore.fetchWelcomeBonus();
    }
  }

  private setupUserEnvironment(profile: Profile) {
    void this.rootStore.userStore.setUserProfile(profile);
    void this.rootStore.rewardsStore.fetchInviteBonusNotification();
    void this.rootStore.gameStore.sendCustomTokenToSlotsGameUi();
    this.rootStore.walletStore.updateWallet(profile.userWallet);
  }

  private setupGuestEnvironment() {
    queryClient.clear();

    void this.rootStore.landingStore.fetchLandingData();
    this.rootStore.rewardsStore.resetDailyBonus();
    this.rootStore.coinTypeStore.setCoinType('GC');
    this.rootStore.walletStore.resetWallet();
    this.rootStore.coinBackStore.resetCoinBack();
    this.rootStore.onboardingStore.setFinishedFtue(false);
    this.rootStore.puzzleStore.resetEvent();
    this.rootStore.hammersStore.resetHammers();
    this.rootStore.trailStore.resetEvent();
    this.rootStore.bingoStore.resetAll();
    this.rootStore.subscriptionStore.resetAll();
    this.rootStore.userFreeSpinsStore.reset();
    this.rootStore.userSnappyGiftsStore.reset();
    this.rootStore.dailyMissionsStore.reset();
    this.rootStore.missionsStore.reset();
    this.rootStore.challengesStore.reset();
    this.rootStore.questStore.reset();
    this.rootStore.tournamentStore.reset();
    this.rootStore.tournamentOfChampionsStore.reset();
    this.rootStore.metagamesStore.reset();
    this.rootStore.prizeDropStore.resetEvent();
    this.rootStore.plinkoStore.reset();
    this.rootStore.rouletteStore.reset();
    this.rootStore.cardWarStore.reset();
    this.rootStore.sportEventsStore.reset();
    this.rootStore.monetizationEventsStore.reset();
    this.rootStore.featureManagerStore.clearPriorityDialogTypes();
    this.rootStore.playingNowStore.reset();

    ReferralService.updateReferralData();

    if (!this.isUserExcluded) {
      this.rootStore.userStore.resetUserProfile();
    }
  }

  private static getAuthStatus(thirdPartyStatus: ThirdPartyStatus['status']): AuthStatus {
    switch (thirdPartyStatus) {
      case 'thirdPartyFirstLogin':
        return 'thirdPartyFirstLogin';
      case 'firstLogin':
      case 'notThirdParty':
      case 'nonFirstLogin':
        return 'authenticated';
      default:
        return 'error';
    }
  }

  @observable
  public phoneVerificationFlowData: { countryCode?: string; phoneNumber: string } | null = null;

  @action
  public setPhoneVerificationFlowData(data: { countryCode?: string; phoneNumber: string } | null) {
    this.phoneVerificationFlowData = data;
  }

  @observable
  public sendVerificationEmailCooldown = 0;

  @observable
  private emailCooldownInterval: NodeJS.Timeout | null = null;

  @observable
  public tabId: string = Math.random().toString(36).slice(2, 9); // Random 7-character string

  @observable
  public currentUserEmail: string | null | undefined = undefined;

  @observable
  public userDataReady = Boolean(auth.userId);

  @observable
  public isAuthenticationFlowDone = false;

  @observable
  public isDisconnectedByMultisession = false;

  @observable
  public authStatus: AuthStatus = 'unauthenticated';

  @observable
  public isAuthWizardOpen = false;

  /** Signed into Firebase with an unverified email — the verify step must be shown, not the logged-out Landing. */
  @observable
  public isEmailVerificationPending = false;

  @observable
  public userCoolDown = auth.userCoolDown;

  @observable
  public flowData?: unknown = undefined;

  @observable
  public directRegEmailVerificationStep = false;

  @observable
  public directRegPostVerificationWizard = false;

  @action
  public setDirectRegEmailVerificationStep(step: boolean) {
    this.directRegEmailVerificationStep = step;
  }

  @action
  public setDirectRegPostVerificationWizard(show: boolean) {
    this.directRegPostVerificationWizard = show;
  }

  @action
  public setIsAuthWizardOpen(isOpen: boolean) {
    this.isAuthWizardOpen = isOpen;

    // Closing any auth wizard (even by a click outside, which keeps the player signed in) ends the pending
    // verify step; otherwise useAuthFlow would reopen it at once. Only the next page load brings it back.
    if (!isOpen) {
      this.isEmailVerificationPending = false;
    }
  }

  @action
  public setIsEmailVerificationPending(isPending: boolean) {
    this.isEmailVerificationPending = isPending;
  }

  @action
  public setAuthStatus(authStatus: AuthStatus) {
    this.authStatus = authStatus;
  }

  @computed
  public get authorized(): boolean {
    return this.userDataReady && this.authStatus === 'authenticated';
  }

  @computed
  public get isUserExcluded(): boolean {
    return this.authStatus === 'userSelfExcluded' || this.authStatus === 'userInactive';
  }

  @computed
  public get isThirdPartyAuthorizing(): boolean {
    return this.pending('thirdPartyAuth');
  }

  @observable
  public refreshTrigger?: symbol = undefined;

  @observable
  private _pending: Record<PendingActionKey, boolean> = {
    signUp: false,
    thirdPartyAuth: false,
    signIn: false,
    resendEmailVerification: false,
    sendPhoneVerification: false,
    completePhoneVerification: false,
    sendPasswordResetLink: false,
    chooseCountry: false,
  };

  private phoneVerificationId: string | undefined = undefined;

  /** The provider that sent the pending code; verify must use the same one even if the flag flips meanwhile. */
  private phoneVerificationProvider: 'firebase' | 'twilio' | undefined = undefined;

  @observable
  public currentUserPhone: string | undefined = undefined;
  @observable
  public kycAccessToken: string | null = null;

  public pending(actionKey: PendingActionKey) {
    return this._pending[actionKey];
  }

  @action
  public setSendVerificationEmailCooldown(cooldown: number) {
    this.sendVerificationEmailCooldown = cooldown;
  }

  @action
  private setUserDataReady(userDataReady: boolean) {
    this.userDataReady = userDataReady;
  }

  @action
  public setIsAuthenticationFlowDone(newStatus: boolean) {
    this.isAuthenticationFlowDone = newStatus;
  }

  @action
  public setFlowData(data?: unknown) {
    this.flowData = data;
  }

  @action
  public setCurrentUserEmail(email?: string | null) {
    this.currentUserEmail = email;
  }

  /** Email the next Reset Password screen starts with; set by a log-in form's force-reset link, read once on open. */
  public passwordResetPrefillEmail: string | null = null;

  /** Email the next log-in form opens with, already in the force-reset state; set by the security email's link. */
  public forceResetLoginEmail: string | null = null;

  @action
  public setCurrentUserPhone(phone?: string) {
    this.currentUserPhone = phone;
  }

  @action
  public setPending(actionKey: PendingActionKey, pending: boolean) {
    this._pending[actionKey] = pending;
  }

  public getLandingKey(): string | null {
    const landingSearchParam = new URLSearchParams(window.location.search).get('landing');
    return landingSearchParam || null;
  }

  public getUtmPromo(): string | null {
    const searchParams = new URLSearchParams(window.location.search);

    const utmPromo = getParam(searchParams, 'utm_promo');
    return utmPromo || null;
  }

  public getUtmFeature(): string | null {
    const searchParams = new URLSearchParams(window.location.search);

    const utmFeature = getParam(searchParams, 'utm_feature');
    return utmFeature || null;
  }

  public isDirectLanding(): boolean {
    const landingKey = this.getLandingKey();
    return (
      !!landingKey &&
      (landingKey.startsWith(LandingKey.DIRECT_SU) ||
        landingKey.startsWith(LandingKey.DIRECT_EM) ||
        landingKey.startsWith(LandingKey.DIRECT_REG))
    );
  }

  public isDirectSignUpLanding(): boolean {
    return this.getLandingKey() === LandingKey.DIRECT_SU || this.isDirectSignUpPrefixLanding();
  }

  public isDirectEmailLanding(): boolean {
    return this.getLandingKey() === LandingKey.DIRECT_EM;
  }

  public isDirectRegLanding(): boolean {
    return this.getLandingKey() === LandingKey.DIRECT_REG;
  }

  public isDirectSignUpPrefixLanding(): boolean {
    const landingKey = this.getLandingKey() || '';
    return landingKey.startsWith(LandingKey.DIRECT_SU) && landingKey !== LandingKey.DIRECT_SU;
  }

  public isSocialLanding(): boolean {
    return this.getLandingKey() === LandingKey.SOCIAL;
  }

  public getDirectSignUpLanderBanner(): string | null {
    if (this.isDirectSignUpPrefixLanding()) {
      const landingKey = this.getLandingKey();
      if (!landingKey) {
        return null;
      }

      const url = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/landing/${landingKey}.webp`;
      return url;
    }

    if (this.isDirectLanding()) {
      const utmPromo = this.getUtmPromo();
      if (!utmPromo) {
        if (this.isDirectEmailLanding()) {
          // default image for direct email landing - 150% offer
          const url = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/landing/${DEFAULT_DIRECT_EM_BANNER}.webp`;
          return url;
        }

        return null;
      }

      const url = `${BUCKET_PREFIX}${ENVIRONMENT}/assets/landing/${utmPromo}.webp`;
      return url;
    }

    return null;
  }

  // sending "updateSearchParams" from outside is necessary to update "useQueryParams" hook listeners
  public clearLandingKey(updateSearchParams: (params: URLSearchParams) => void) {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('landing')) {
      queryParams.delete('landing');
      updateSearchParams(queryParams);
    }
  }

  public setAuthCompleted() {
    this.setAuthStatus('authenticated');
  }

  public async chooseCountry(data: Pick<Profile, 'language'> & Pick<Territory, 'countryCode'>) {
    try {
      this.setPending('chooseCountry', true);
      const result = await this.updateUserProfile(data);
      const updatedProfile = result.data.data ?? {};
      const { territory, language } = updatedProfile;

      if (!result.errors?.length) {
        this.rootStore.userStore.updateProfile({
          isCountrySetByUser: true,
          territory,
          language,
        });

        await this.refreshAfterTerritoryChange(updatedProfile);
      }

      return result;
    } finally {
      this.setPending('chooseCountry', false);
    }
  }

  /**
   * Players who sign up from a territory we don't operate in are created with the geo-derived
   * territory (e.g. IL) and only pick their real one on the choose-country step. Everything
   * territory-scoped was already fetched under the old territory by then, so it has to be
   * re-evaluated here:
   *  - LaunchDarkly is re-identified (awaited) so flags gated on `country_code` — missions among
   *    them — resolve against the new territory before anything reads them again.
   *  - The lobby categories and the feature-manager stores are refetched, since `user/sub-category`
   *    and the feature fetches are geo-scoped server-side.
   * Never let a failure here surface as a failed country change: the profile is already saved.
   */
  private async refreshAfterTerritoryChange(updatedProfile: Partial<Profile>) {
    try {
      // Guard on firebaseUid: createLDContext falls back to its anonymous branch without one, so a
      // truncated response would downgrade the context instead of re-pointing it at the new territory.
      if (ldClient && updatedProfile.firebaseUid) {
        await identifyUser(createLDContext(updatedProfile as Profile, this.rootStore.liveGeoStore.rawGeo));
      }

      // Kicks off refetches for active queries; not awaited so the wizard isn't blocked on all of them.
      void queryClient.invalidateQueries();

      await this.rootStore.categoriesStore.fetchCategories();

      // `fetchStores` latches `loadingByCoinType` before checking `dialogsOrder`, so calling it
      // before the feature manager has initialized would wedge every later fetch.
      if (this.rootStore.featureManagerStore.storeStatus === 'READY') {
        await this.rootStore.featureManagerStore.fetchStores({ isCoinChange: false });
        this.rootStore.featureManagerStore.startMainSequence();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error(`Failed to refresh territory-scoped data after country change: ${errorMessage}`);
    }
  }

  public async updateUserProfile(profile: Partial<Profile>) {
    return await postRequest<ApiResponse<Profile>>('user/profile', profile);
  }

  public async authWithThirdPartyPlatform(platform: 'apple' | 'google' | 'facebook') {
    this.setPending('thirdPartyAuth', true);
    setTimeout(() => {
      this.setPending('thirdPartyAuth', false);
    }, 2000);

    try {
      const user = await auth.authWithThirdPartyPlatform(platform);
      // A redirect reloads the page and takes the login dialog with it; a popup leaves it open.
      if (user && auth.usesPopupSignIn) {
        this.rootStore.portalStore.closePortal();
      }
      return user;
    } finally {
      this.setPending('thirdPartyAuth', false);
    }
  }

  /**
   * Provider sign-in from any entry point: the shell's native sheet inside the mobile shell, the web flow
   * everywhere else. Inside the shell `signInWithRedirect` cannot work — it needs sessionStorage to survive a
   * full navigation to the provider and back, which WebView storage partitioning defeats — so no caller
   * should pick between the two itself.
   */
  public authWithProvider(platform: 'apple' | 'google' | 'facebook') {
    return isInShell() ? this.authWithNativeProvider(platform) : this.authWithThirdPartyPlatform(platform);
  }

  public async authWithNativeProvider(platform: 'apple' | 'google' | 'facebook') {
    // Pending for the whole sheet, with no 2s release like the redirect flow's: the page stays live
    // behind a native sheet, and a button re-enabled mid-sheet lets a second tap start a second flow.
    this.setPending('thirdPartyAuth', true);

    try {
      const user = await auth.authWithNativeProvider(platform);
      // Like a popup, the native sheet leaves this page — and any dialog that opened it — in place. Only
      // when one is open: `closePortal` plays its close sound, and the native lander signs in from no dialog.
      if (user && this.rootStore.portalStore.isVisible) {
        this.rootStore.portalStore.closePortal();
      }
      return user;
    } finally {
      this.setPending('thirdPartyAuth', false);
    }
  }

  public async signInWithEmail(data: { email: string; password: string }) {
    this.setPending('signIn', true);
    this.setIsAuthenticationFlowDone(false);

    try {
      const user = await auth.signInWithEmail(data);
      this.setCurrentUserEmail(data.email);

      return user;
      /* If logIn errors out, hide the Pending overlay immediately and re-throw the error so that it can be displayed in
       the UI. Otherwise, the overlay will only be hidden in the `auth.subscribeToAuthStateChange` callback, after
       all the necessary on-auth checks and API calls are performed.
    */
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      this.setPending('signIn', false);
    }
  }

  public clearSendVerificationEmailCooldown() {
    if (this.emailCooldownInterval) {
      clearInterval(this.emailCooldownInterval);
      this.emailCooldownInterval = null;
    }

    this.setSendVerificationEmailCooldown(0);
  }

  private async sendVerificationEmail() {
    this.clearSendVerificationEmailCooldown();

    this.setSendVerificationEmailCooldown(60);

    const response = await auth.sendEmailVerification();

    this.emailCooldownInterval = setInterval(() => {
      if (this.sendVerificationEmailCooldown === 0 && this.emailCooldownInterval) {
        this.clearSendVerificationEmailCooldown();
      } else if (this.sendVerificationEmailCooldown > 0) {
        this.setSendVerificationEmailCooldown(this.sendVerificationEmailCooldown - 1);
      }
    }, 1000);

    return response;
  }

  public async signUpWithEmail(credentials: { email: string; password: string }) {
    this.setPending('signUp', true);

    try {
      const user = await auth.signUpWithEmail(credentials);
      this.setCurrentUserEmail(credentials.email);

      if (!user.emailVerified) {
        await this.sendVerificationEmail();
      }

      return user;
    } finally {
      this.setPending('signUp', false);
    }
  }

  public async resendEmailVerification() {
    this.setPending('resendEmailVerification', true);
    try {
      return await this.sendVerificationEmail();
    } finally {
      this.setPending('resendEmailVerification', false);
    }
  }

  public trackEmailVerificationCompletion(callback?: (isSuccess: boolean) => void) {
    return auth.trackVerificationCompletion(async () => {
      const { isSuccess } = await this.handleVerificationCompletion();
      if (typeof callback === 'function') {
        callback(isSuccess);
      }
    });
  }

  public async sendPasswordResetLink(email: string) {
    this.setPending('sendPasswordResetLink', true);

    try {
      return await auth.sendPasswordResetLink(email);
    } finally {
      this.setPending('sendPasswordResetLink', false);
    }
  }

  @action
  public resyncUserCooldown() {
    this.userCoolDown = auth.userCoolDown;
  }

  @action
  public clearUserCooldown() {
    auth.userCoolDown = null;
    this.userCoolDown = null;
  }

  public async handleVerificationCompletion() {
    // Firebase does not re-notify the auth listener when the same user becomes verified, so clear it here;
    // otherwise closing the wizard would reopen the verify step.
    this.setIsEmailVerificationPending(false);

    let profile;

    try {
      profile = await auth.apiLogin('email');

      if (profile) {
        auth.sendRegisterSuccessEvent('email', profile.email, profile);
      }
      return { isSuccess: true };
    } catch (error) {
      const err = error as Error;

      void logToNewRelic('Failed to handle verification completion', 'Error in handleVerificationCompletion', 'error', {
        error: {
          message: err.message,
        },
      });

      this.setUserDataReady(false);
      return { isSuccess: false };
    } finally {
      if (profile) {
        this.setUserDataReady(true);
        this.setupUserEnvironment(profile);

        if (!socket.connected) {
          socket.connect();
        }
      }
      // After the block above: fetchCategories picks its route from userDataReady.
      void this.rootStore.categoriesStore.fetchCategories();
      if (auth.isAuthError) {
        this.setAuthStatus('authError');
      }

      if (auth.isUserInactive) {
        this.setAuthStatus('userInactive');
      }

      if (auth.isUserSelfExcluded) {
        this.setAuthStatus('userSelfExcluded');
      }
    }
  }

  private get isTwilioPhoneVerification() {
    return resolveFlag<string>('phone_verification_provider', 'firebase') === 'twilio';
  }

  private async startTwilioPhoneVerification(
    phone: string,
  ): Promise<{ exceededRegistrationLimit?: boolean; verified?: boolean }> {
    this.setPending('sendPhoneVerification', true);

    try {
      const status = await auth.startPhoneOtp(phone);

      if (status === 'exceededRegistrationLimit') {
        return { exceededRegistrationLimit: true };
      }

      this.phoneVerificationProvider = 'twilio';
      this.setCurrentUserPhone(phone);
      return {};
    } catch (e) {
      if (e instanceof PhoneOtpError && e.errorCode === PHONE_OTP_ALREADY_VERIFIED) {
        return { verified: true };
      }
      throw e;
    } finally {
      this.setPending('sendPhoneVerification', false);
    }
  }

  public async sendPhoneVerificationCode(
    phone: string,
  ): Promise<{ exceededRegistrationLimit?: boolean; verified?: boolean }> {
    if (this.isTwilioPhoneVerification) {
      return this.startTwilioPhoneVerification(phone);
    }

    this.setPending('sendPhoneVerification', true);
    const appVerifier = auth.getRecaptchaVerifier();

    try {
      const { isInUse, verified, exceededRegistrationLimit } = await auth.checkIfPhoneVerified(phone);

      if (exceededRegistrationLimit) {
        return { exceededRegistrationLimit };
      }

      if (isInUse) {
        throw new Error(t('phoneVerification.phoneNumberAlreadyInUseError'));
      }

      if (verified) {
        return { verified };
      }

      this.phoneVerificationId = await auth.sendPhoneVerificationCode(phone, appVerifier);
      this.phoneVerificationProvider = 'firebase';
      this.setCurrentUserPhone(phone);
      return {};
    } finally {
      this.setPending('sendPhoneVerification', false);
      try {
        appVerifier.clear();
      } catch {}
    }
  }

  public async completePhoneVerification(verificationCode: string) {
    const provider = this.phoneVerificationProvider;
    const isTwilio = provider === 'twilio';

    if (!provider || (isTwilio ? !this.currentUserPhone : !this.phoneVerificationId)) {
      throw new Error('Phone verification process has not been started yet.');
    }

    this.setPending('completePhoneVerification', true);

    try {
      const countryCode = this.phoneVerificationFlowData?.countryCode ?? undefined;

      if (isTwilio) {
        let newGeo: CleanGeoData;
        try {
          ({ newGeo } = await auth.verifyPhoneOtp(this.currentUserPhone!, verificationCode, countryCode));
        } catch (e) {
          // A retry or double submit after the first verify landed: the phone is verified, so show that.
          if (e instanceof PhoneOtpError && e.errorCode === PHONE_OTP_ALREADY_VERIFIED) {
            await this.reloadVerifiedProfile();
            return null;
          }
          throw e;
        }
        await this.applyVerifiedPhone(newGeo);
        return null;
      }

      const verificationResult = await auth.verifyPhone(this.phoneVerificationId!, verificationCode, countryCode);
      await this.applyVerifiedPhone(verificationResult.newGeo);
      return verificationResult.userCredential;
    } finally {
      this.setPending('completePhoneVerification', false);
    }
  }

  private async refreshPackagesIfCountryChanged(previousCountry?: string, nextCountry?: string) {
    if (previousCountry === nextCountry) return;
    try {
      // We need to await this so that by the time `openOrder` is called a new list is already there
      await this.rootStore.appStore.fetchStorePackages();
    } catch (packagesErr) {
      devError('Failed to refresh packages on country change', packagesErr);
    }
  }

  private async reloadVerifiedProfile() {
    const previousCountry = this.rootStore.userStore.profile?.cleanGeoData?.countryCode;
    const profile = await this.rootStore.userStore.fetchProfile(true);
    await this.refreshPackagesIfCountryChanged(previousCountry, profile?.cleanGeoData?.countryCode);
  }

  private async applyVerifiedPhone(newGeo: CleanGeoData) {
    await this.refreshPackagesIfCountryChanged(
      this.rootStore.userStore.profile?.cleanGeoData?.countryCode,
      newGeo.countryCode,
    );

    if (this.currentUserPhone) {
      const profileUpdates: Partial<Profile> = {
        phone: this.currentUserPhone,
        phoneVerified: true,
        cleanGeoData: newGeo,
      };

      this.rootStore.userStore.updateProfile(profileUpdates);
    } else {
      devError('User phone number does not exist');
    }
  }

  @action
  public setKycAccessToken(token: string | null) {
    this.kycAccessToken = token;
  }

  public async fetchPlayerKycAccessToken(onFailure?: (stores: RootStore, apiError?: unknown) => void): Promise<string> {
    try {
      const response = await getRequest('user/init-access-token');

      const token = get(response, 'data.token', null);
      this.setKycAccessToken(token || null);

      if (!token && typeof onFailure === 'function') {
        onFailure(this.rootStore);
      }

      return token || '';
    } catch (apiError) {
      if (typeof onFailure === 'function') {
        onFailure(this.rootStore, apiError);
      }

      this.setKycAccessToken(null);
      return '';
    }
  }

  public async getKycAccessToken() {
    try {
      const response = await getRequest('user/init-access-token');

      const token = get(response, 'data.token', null);

      if (token) {
        this.setKycAccessToken(token);
      } else {
        this.rootStore.payRedeemStore.setIsRedeemKYCFailed(true);
      }
    } catch (error) {
      this.rootStore.payRedeemStore.setIsRedeemKYCFailed(true);
    }
  }
}
